// apps/storefront/src/features/cart/api.ts
// ─────────────────────────────────────────────────────────────────
// API layer para cart sync con el backend.
// Soporta guest (x-guest-session-id header) y customer (Bearer token).
// ─────────────────────────────────────────────────────────────────

import { api, buildStorefrontApiPath } from "@/lib/api";
import { peekGuestSessionId } from "@/hooks/useGuestSession";
import { useAuthStore } from "@/store/auth";

// ─── Types ────────────────────────────────────────────────────────────────────

export type ServerCartItemDto = {
  productId: string;
  slug: string;
  name: string;
  imageUrl: string | null;
  /**
   * Precio en cents. Sprint 12 Slice 3: ya viene resuelto por el helper
   * backend (wholesalePrice si las 3 condiciones se cumplen, salePrice en
   * cualquier otro caso). El frontend NO recalcula precios.
   */
  price: number;
  currency: string;
  quantity: number;
  maxStock: number;
  available: boolean;
  // Sprint 12 Slice 3 — indicador wholesale per-ítem.
  /** true si el precio cobrado es el mayorista. */
  isWholesaleApplied: boolean;
  /** Umbral configurado en el producto (null = sin umbral o sin wholesale). */
  wholesaleMinQty: number | null;
  /**
   * Unidades que faltan para alcanzar el mínimo. null si ya aplica o si el
   * customer no es elegible al mayoreo de este producto.
   */
  qtyToUnlock: number | null;
};

export type ServerCartDto = {
  items: ServerCartItemDto[];
  subtotalCents: number;
  currency: string;
  itemCount: number;
  purgedCount: number;
};

type CartSyncItem = {
  productId: string;
  quantity: number;
};

// ─── Envelope unwrap ──────────────────────────────────────────────────────────

type Envelope<T> = { data: T } | T;

function unwrap<T>(raw: Envelope<T>): T {
  return (raw as { data: T }).data ?? (raw as T);
}

// ─── Headers ──────────────────────────────────────────────────────────────────

function buildCartHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};

  const token = useAuthStore.getState().accessToken;
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  // Siempre enviar guest session id si existe — el backend decide qué usar
  const guestId = peekGuestSessionId();
  if (guestId) {
    headers["x-guest-session-id"] = guestId;
  }

  return headers;
}

// ─── API functions ────────────────────────────────────────────────────────────

/**
 * GET /cart — Lee el cart validado del server.
 */
export async function fetchServerCart(): Promise<ServerCartDto> {
  const res = await api.get<Envelope<ServerCartDto>>(
    buildStorefrontApiPath("/cart"),
    { headers: buildCartHeaders() },
  );
  return unwrap(res.data);
}

/**
 * PUT /cart — Sync completo. Envía la lista de items local y recibe el cart validado.
 */
export async function syncServerCart(
  items: CartSyncItem[],
): Promise<ServerCartDto> {
  const res = await api.put<Envelope<ServerCartDto>>(
    buildStorefrontApiPath("/cart"),
    { items },
    { headers: buildCartHeaders() },
  );
  return unwrap(res.data);
}

/**
 * DELETE /cart — Vaciar carrito en el server.
 */
export async function clearServerCart(): Promise<void> {
  await api.delete(buildStorefrontApiPath("/cart"), {
    headers: buildCartHeaders(),
  });
}
